/** @jsxImportSource @emotion/react */
import { withMyTheme } from "../../../theme/theme"
import { css } from "@mui/material"
import { Typography, Box, Switch, FormControlLabel } from "@mui/material"
import { useTranslation } from "react-i18next"
import { mobileCss } from "../../../util/isMobile"
import { useQuestionnaireForm } from "../context/QuestionnaireFormContext"
import { useState } from "react"
import { QuestionSelectorButton } from "./components/QuestionSelectorButton"
import { QuestionSelectorModal } from "./components/QuestionSelectorModal"
import { LocationDetailsField } from "./components/LocationDetailsField"

const StepTitleStyle = withMyTheme((theme) => css`
    font-size: 2rem;
    font-weight: 600;
    color: ${theme.palette.primary.main};
    text-align: center;
    margin-bottom: 1rem;
    
    ${mobileCss(`
        font-size: 1.5rem;
    `)}
`)

const StepContentStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1.5vh;
    text-align: center;
    margin: 0 auto;
    padding: 0 1rem;
    
    ${mobileCss(`
        max-width: 90vw;
        gap: 1.5rem;
    `)}
`)

const FormContainerStyle = withMyTheme((theme) => css`
    display: flex;
    flex-direction: column;
    gap: 2rem;
    width: 50vw;
    
    ${mobileCss(`
        gap: 1.5rem;
        width: 80vw;
    `)}
`)

const NoSelectionMessageStyle = withMyTheme((theme) => css`
    text-align: center;
    color: ${theme.palette.text.secondary};
    font-style: italic;
    padding: 2rem;
`)

const ToggleContainerStyle = withMyTheme(() => css`
    display: flex;
    justify-content: center;
    margin-bottom: 0.5rem;
`)

const ToggleLabelStyle = withMyTheme((theme) => css`
    .MuiFormControlLabel-label {
        font-family: ${theme.typography.body1.fontFamily};
        font-size: 1.1rem;
        font-weight: 500;
        color: ${theme.palette.text.primary};
    }
`)

const SwitchStyle = withMyTheme((theme) => css`
    & .MuiSwitch-switchBase.Mui-checked {
        color: ${theme.palette.primary.main};
    }
    & .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track {
        background-color: ${theme.palette.primary.main};
    }
`)

interface GuestsFAQStepProps {
    onStart?: () => void
}

export const GuestsFAQStep = ({}: GuestsFAQStepProps) => {
    const { t } = useTranslation()
    const { formData, updateGuestsFAQ, addFAQItem, updateFAQAnswer } = useQuestionnaireForm()
    const [modalOpen, setModalOpen] = useState(false)

    const selectedFAQ = formData.guestsFAQ.faqs.find(
        faq => faq.id === formData.guestsFAQ.selectedQuestionId
    )

    const handleOpenModal = () => {
        setModalOpen(true)
    }

    const handleCloseModal = () => {
        setModalOpen(false)
    }

    const handleSelectQuestion = (question: string, isCustom: boolean) => {
        // Check if this question already exists
        const existingFAQ = formData.guestsFAQ.faqs.find(faq => faq.question === question)
        
        if (existingFAQ) {
            // If exists, just select it
            updateGuestsFAQ({ selectedQuestionId: existingFAQ.id })
        } else {
            // If new, add it
            const newFAQ = {
                id: `faq-${Date.now()}`,
                question,
                answer: '',
                isCustom
            }
            addFAQItem(newFAQ)
        }
    }

    const handleAnswerChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (formData.guestsFAQ.selectedQuestionId) {
            updateFAQAnswer(formData.guestsFAQ.selectedQuestionId, event.target.value)
        }
    }

    const isEnabled = formData.guestsFAQ.enabled !== false

    const handleToggle = () => {
        updateGuestsFAQ({ enabled: !isEnabled })
    }

    return (
        <div css={StepContentStyle}>
            <Typography css={StepTitleStyle}>
                {t('questionnaire.guestsFAQ.title')}
            </Typography>

            <Box css={ToggleContainerStyle}>
                <FormControlLabel
                    css={ToggleLabelStyle}
                    control={
                        <Switch
                            css={SwitchStyle}
                            checked={isEnabled}
                            onChange={handleToggle}
                        />
                    }
                    label={t('questionnaire.guestsFAQ.enableToggle')}
                />
            </Box>

            {isEnabled && (
                <>
                    <Box css={FormContainerStyle}>
                        <QuestionSelectorButton
                            onClick={handleOpenModal}
                            selectedQuestion={selectedFAQ?.question}
                        />

                        {selectedFAQ ? (
                            <LocationDetailsField
                                label={t('questionnaire.guestsFAQ.answerLabel')}
                                placeholder={t('questionnaire.guestsFAQ.answerPlaceholder')}
                                value={selectedFAQ.answer}
                                onChange={handleAnswerChange}
                            />
                        ) : (
                            <Typography css={NoSelectionMessageStyle}>
                                {t('questionnaire.guestsFAQ.noQuestionSelected')}
                            </Typography>
                        )}
                    </Box>

                    <QuestionSelectorModal
                        open={modalOpen}
                        onClose={handleCloseModal}
                        onSelectQuestion={handleSelectQuestion}
                        addedQuestions={formData.guestsFAQ.faqs}
                    />
                </>
            )}
        </div>
    )
}
